
import { useState, useEffect, useCallback } from "react";
import { fetchMyReagents, fetchAllUserSwapRequests, reactivateReagent } from "@/services/myReagents";
import { Reagent } from "@/types/reagent.types";
import { SwapRequest } from "@/services/myReagents";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";

/**
 * Custom hook for handling reagent loading and management in MyReagentsPage
 */
export const useMyReagents = () => {
  const { user } = useAuth();
  
  // Data state
  const [myReagents, setMyReagents] = useState<Reagent[]>([]);
  const [removedReagents, setRemovedReagents] = useState<Reagent[]>([]);
  const [swapRequests, setSwapRequests] = useState<SwapRequest[]>([]);
  
  // Loading states
  const [loadingReagents, setLoadingReagents] = useState(true);
  const [loadingRemovedReagents, setLoadingRemovedReagents] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);
  
  // Error state
  const [error, setError] = useState<string | null>(null);
  
  // Load my reagents (both available and unavailable)
  const loadMyReagents = useCallback(async () => {
    console.log("Loading my reagents...");
    setLoadingReagents(true);
    setError(null);
    try {
      const reagents = await fetchMyReagents();
      console.log("Loaded reagents:", reagents.length);
      
      // Separate available and unavailable reagents
      const available = reagents.filter(r => r.available);
      const unavailable = reagents.filter(r => !r.available);
      
      setMyReagents(available);
      setRemovedReagents(unavailable);
    } catch (err) {
      console.error("Error loading reagents:", err);
      setError("Failed to load reagents. Please try again.");
      toast.error("Failed to load your reagents");
    } finally {
      setLoadingReagents(false);
      setLoadingRemovedReagents(false);
    }
  }, []);
  
  // Load swap requests
  const loadSwapRequests = useCallback(async () => {
    console.log("Loading swap requests...");
    setLoadingRequests(true);
    try {
      const requests = await fetchAllUserSwapRequests();
      console.log("Loaded swap requests:", requests.length);
      setSwapRequests(requests);
    } catch (err) {
      console.error("Error loading swap requests:", err);
      setError("Failed to load swap requests. Please try again.");
      toast.error("Failed to load swap requests");
    } finally {
      setLoadingRequests(false);
    }
  }, []);
  
  // Handle reactivating a reagent
  const handleReactivateReagent = useCallback(async (reagentId: string) => {
    setReactivatingId(reagentId);
    try {
      const success = await reactivateReagent(reagentId);
      if (success) {
        // Reload reagents to reflect changes
        await loadMyReagents();
        toast.success("Reagent is now available again");
      }
    } finally {
      setReactivatingId(null);
    }
  }, [loadMyReagents]);
  
  // Refresh all data
  const refreshData = useCallback(() => {
    toast.info("Refreshing data...");
    loadMyReagents();
    loadSwapRequests();
  }, [loadMyReagents, loadSwapRequests]);
  
  // Initial data loading when user is authenticated
  useEffect(() => {
    if (user) {
      console.log("User authenticated, loading data for:", user.id);
      
      // Add a small delay to ensure auth is fully initialized
      const timer = setTimeout(() => {
        loadMyReagents();
        loadSwapRequests();
      }, 500);
      
      return () => clearTimeout(timer);
    } else {
      console.log("No authenticated user found");
    }
  }, [user, loadMyReagents, loadSwapRequests]);
  
  // Derived data
  const pendingRequestsCount = swapRequests.filter(req => req.status === "pending").length;
  const removedReagentsCount = removedReagents.length;
  
  return {
    // Data
    myReagents,
    removedReagents,
    swapRequests,
    
    // Loading states
    loadingReagents,
    loadingRemovedReagents,
    loadingRequests,
    reactivatingId,
    
    // Error state
    error,
    
    // Derived data
    pendingRequestsCount,
    removedReagentsCount,
    
    // Actions
    loadMyReagents,
    loadSwapRequests,
    handleReactivateReagent,
    refreshData
  };
};
